/**
 * Registry model (P0.1 registry-first): ALL domain data — services, labels,
 * add-ons, bands, statuses — arrives over the registry endpoints at login and
 * is read through typed accessors. The client NEVER hardcodes an enumeration;
 * a hand-typed service/band/status list in client code is a defect.
 *
 * Types are imported `import type` from the shared schema modules — they
 * vanish at build time (the DATA still arrives over HTTP; only the SHAPES are
 * shared, which is exactly the registry-first contract).
 */
import type { ClientAddOnDescriptor, AddOnKey } from '../../shared/addons-schema.js';
import type { ServiceDescriptor } from '../../shared/service-schema.js';
import type { clientStatusRegistry } from '../../shared/status-schema.js';
import type { Channel } from '../../shared/send-schema.js';
import { getJson } from '../lib/api.js';

export type StatusRegistry = ReturnType<typeof clientStatusRegistry>;

export type DescriptorsResponse = {
  services: ServiceDescriptor[];
  labels: Record<string, string>;
  commonRequired: string[];
  addons: ClientAddOnDescriptor[];
  statuses: StatusRegistry;
  /** T407: ids offered for NEW work (tiles filter on it). Optional so the 7
   *  registry fixtures + an older server stay valid; absent = all offered. */
  offeredServiceIds?: string[];
  /**
   * T524 (C5): which customer channels the SERVER can actually send on.
   *
   * Optional for the same reason `offeredServiceIds` is, one line up: every
   * hand-built registry fixture and any older server stays valid, and absent
   * means "assume everything works".
   *
   * AN OBJECT MAP OF BOOLEANS, not a `string[]` of configured names, and the
   * choice is load-bearing rather than stylistic. With a map the accessor is
   * `configuredChannels?.[c] !== false`, so an absent field, an empty object AND
   * an unmentioned channel all read as configured, and ONLY an explicit `false`
   * marks a channel dead — fail-open is total rather than a special case. A
   * `string[]` would have to treat "empty" as a magic fail-open value, which is
   * the `stub-registry-empty-bands` trap in a new costume: a fixture carrying
   * `[]` would read as "no channel exists" and blank the picker across the
   * suite. The `offeredServiceIds?` precedent is being borrowed for its
   * OPTIONALITY discipline, not its array type.
   */
  configuredChannels?: Partial<Record<Channel, boolean>>;
};

export type OptionsResponse = {
  services: string[];
  /**
   * The priced airport towns. T660 M9 (P0-27) added `region` — the area the
   * browse chips group by.
   *
   * OPTIONAL, for the reason `offeredServiceIds?` and `configuredChannels?`
   * above are: every hand-built registry fixture in the suite carries
   * `airportPickups: []` or omits the field's shape, and an older server does
   * not send it. Absent reads as "area unknown", which groups under `Other` —
   * never as "this town does not exist".
   */
  airportPickups: { locationId: string; label: string; region?: string | null }[];
  golfHubs: { locationId: string; label: string }[];
  /**
   * T906 S1 — the ONE golf place list, and the priced pairs over it.
   *
   * OPTIONAL for the same reason `airportPickups` is: every hand-built registry
   * fixture in the suite predates them, and an older server does not send them.
   * Absent reads as "no places / nothing priced", which degrades every golf
   * pair to a hand price — never to a wrong price.
   *
   * `golfPlaces` carries NO id: a course name and Dublin Airport have no
   * `locations` row, and inventing one would make `pickupEndDisplay` collapse
   * an operator-typed 'X — Y'. A place's identity is its name.
   */
  golfPlaces?: { name: string; kind: 'hub' | 'course' | 'airport' }[];
  golfPairs?: { locationId: string; locationLabel: string; a: string; b: string }[];
  bands: string[];
  meta: Record<string, string>;
};

export type Registry = {
  descriptors: DescriptorsResponse;
  options: OptionsResponse;
};

/**
 * Fetch the registry once at login. THROWS on failure — the app must not
 * proceed on a partial registry (silent-empty here would mislead every
 * screen; the login flow surfaces friendly retry copy).
 */
export async function loadRegistry(): Promise<Registry> {
  const [descriptors, options] = await Promise.all([
    getJson<DescriptorsResponse>('/api/service-descriptors'),
    getJson<OptionsResponse>('/api/options'),
  ]);
  return { descriptors, options };
}

/**
 * Fresh `app_meta` snapshot (add-on prices, rule statuses). The login-time
 * registry copy goes stale the moment an operator saves a price — the admin
 * section re-reads through this on every view entry, so no admin view can
 * show a pre-change figure after a write (T387 rework 1).
 */
export async function loadMeta(): Promise<OptionsResponse['meta']> {
  return (await getJson<OptionsResponse>('/api/options')).meta;
}

// ---------------------------------------------------------------------------
// Typed accessors — screens read the registry ONLY through these
// ---------------------------------------------------------------------------

export function serviceById(reg: Registry, serviceId: string): ServiceDescriptor | null {
  return reg.descriptors.services.find((s) => s.serviceId === serviceId) ?? null;
}

export function serviceLabel(reg: Registry, serviceId: string): string {
  return reg.descriptors.labels[serviceId] ?? serviceId;
}

/** Home-screen tiles: descriptor order IS display order (zero client lists).
 *  T407: retired services (offeredServiceIds) get no tile — historical rows
 *  still resolve via serviceById/serviceLabel, which read the FULL list. */
export function serviceTiles(reg: Registry): { serviceId: string; label: string }[] {
  const offered = reg.descriptors.offeredServiceIds;
  return reg.descriptors.services
    .filter((s) => offered === undefined || offered.includes(s.serviceId))
    .map((s) => ({ serviceId: s.serviceId, label: s.label }));
}

export function bands(reg: Registry): string[] {
  return reg.options.bands;
}

export function addOns(reg: Registry): ClientAddOnDescriptor[] {
  return reg.descriptors.addons;
}

export function addOnByKey(reg: Registry, key: AddOnKey): ClientAddOnDescriptor | null {
  return reg.descriptors.addons.find((a) => a.engineKey === key) ?? null;
}

/** Add-ons VISIBLE for a service come from the service descriptor's
 *  allowedAddOns keys (M3 correction: NOT `validFor`, which is settability
 *  and always 'all'; `pricesFor` is pricing applicability only). */
export function addOnsForService(reg: Registry, serviceId: string): ClientAddOnDescriptor[] {
  const svc = serviceById(reg, serviceId);
  if (!svc) return [];
  const allowed = new Set<string>(svc.allowedAddOns);
  return reg.descriptors.addons.filter((a) => allowed.has(a.engineKey));
}

/** [engineKey, label] pairs for every registry add-on (chip rendering). */
export function addOnLabelPairs(reg: Registry): [string, string][] {
  return reg.descriptors.addons.map((a) => [a.engineKey, a.label]);
}

export function bookingStatusLabel(reg: Registry, status: string): string {
  return (reg.descriptors.statuses.booking.labels as Record<string, string>)[status] ?? status;
}

export function paymentStatusLabel(reg: Registry, status: string): string {
  return (reg.descriptors.statuses.payment.labels as Record<string, string>)[status] ?? status;
}

export function sendStatusLabel(reg: Registry, status: string): string {
  return (reg.descriptors.statuses.send.labels as Record<string, string>)[status] ?? status;
}

export function bookingTypeLabel(reg: Registry, bookingType: string): string {
  return reg.descriptors.statuses.bookingTypes[bookingType] ?? bookingType;
}

/**
 * The operator label for a send CHANNEL (T549 M7) - so a delivery status can
 * say WHICH channel it belongs to, and the channel picker can name its options.
 *
 * `?? channel` is not decoration here, it is the contract: this is a RENDER
 * accessor, never a validity test. Membership of the registry says nothing
 * about whether a channel may be used - the send path owns that - and every
 * hand-built test fixture in this repo carries `channels: { values: [], labels:
 * {} }`, so a caller that gated on presence would read "no channel exists"
 * under all of them. An unlabelled channel renders as its raw value, exactly
 * like the four sibling accessors above.
 *
 * `Object.hasOwn`, not a bare index — the fix `shared/send-schema.ts`'s
 * `contactPreferenceLabel` already documents for the same shape. A bare index
 * walks the prototype, so 'constructor' returns a FUNCTION and the `?? channel`
 * fallback above never fires. Unreachable today (the value comes from the
 * ledger), but the fallback IS the contract here, so it must hold for every
 * string.
 */
export function channelLabel(reg: Registry, channel: string): string {
  const labels = reg.descriptors.statuses.channels.labels as Record<string, string>;
  return Object.hasOwn(labels, channel) ? labels[channel]! : channel;
}

/**
 * Can the SERVER actually send on this channel right now? (T524, C5.)
 *
 * FAIL-OPEN BY CONSTRUCTION, and that is the whole design. `!== false` means an
 * absent field, an empty object and an unmentioned channel all answer `true`;
 * only an explicit `false` answers `false`. Nothing else in this accessor can
 * make a channel disappear.
 *
 * Fail-open is not laziness here, it is the correct failure direction twice
 * over. SendQuote.tsx says in as many words that every hand-built fixture in the
 * suite carries empty channel values and "a validity gate would read those as
 * 'no channel exists'" - so a fail-CLOSED accessor blanks the picker across the
 * test suite. And if the server key is ever dropped or renamed, fail-closed
 * would blank it in PRODUCTION, taking away Helen's ability to send at all
 * because a hint about configuration went missing. The worst a fail-open miss
 * can do is pre-select a channel that then 503s with operator copy, which is
 * exactly today's behaviour.
 *
 * It answers "is it configured", NEVER "is it offerable" - `offered` is still
 * IMPLEMENTED_CHANNELS filtered by having a recipient, and nothing here hides an
 * option.
 *
 * ⚠️ WHAT THE ANSWER DECIDES CHANGED IN T660 M4 (P0-6). This used to close with
 * "Helen may know SMS is about to be switched on and pick it anyway" - i.e. the
 * answer picked a DEFAULT and nothing more. M4 promoted it: SendQuote's
 * `selectable` gates SELECTION on it as well, so a `false` here now announces
 * that radio `aria-disabled`, renders a visible reason under it and makes every
 * activation route a no-op. Hiding is still forbidden and `offered` is still
 * untouched; what a `false` costs Helen is only the ability to MOVE the
 * selection onto a transport that cannot carry the message.
 *
 * That RAISES the price of a wrong `false`, so the fail-open construction above
 * matters more now than it did at C5, not less: a dropped or renamed server key
 * must never be able to dim every option and strand her.
 */
export function isChannelConfigured(reg: Registry, channel: Channel): boolean {
  return reg.descriptors.configuredChannels?.[channel] !== false;
}

/*
 * ⛔ T651 M7 — `instanceId(reg)` WAS HERE AND IS GONE, DELIBERATELY NOT KEPT
 * "in case". It read /api/options' `meta.instance_id` for exactly one purpose:
 * namespacing the nursing-home localStorage capture key by database (T414 F4),
 * so a capture written against one database could never be read back against
 * another that reused the same sequential booking id. That store
 * (lib/nursingHomeCaptureStore.ts) is deleted - the schedule lives in
 * `booking_series` and is read back by booking id through the server, which
 * cannot confuse two databases because it IS one of them. The accessor had no
 * other caller, so it orphaned completely.
 *
 * ⚠️ THE SERVER HALF IS DELIBERATELY LEFT ALONE. core/data/rates-repo.ts's
 * `ensureInstanceId` still mints the app_meta row and /api/options still serves
 * it. Removing that would change the shape of a response every registry fixture
 * and several e2e specs read, to delete a key that costs nothing and is the
 * obvious identifier for the next thing that needs one.
 */
